// Portfolio Drift & Rebalancing Alpha Auditor -- TanStack Query hook for the
// PUBLIC synchronous audit endpoint.
//
// AUTH / BASE-URL NOTE: unauthenticated marketing endpoint, so -- exactly like
// the sibling Portfolio Diversification Checker and the free calculators -- we
// reuse apiClient's resolved baseURL via a clean, interceptor-free axios
// instance (no Bearer token, no 401 redirect).

import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { apiClient } from '../../api/client';
import type { CoverageResponse, DriftAuditRequest, DriftAuditResponse } from './types';

const publicApiClient = axios.create({
    baseURL: apiClient.defaults.baseURL,
    // The audit runs three Numba simulations + SciPy math server-side; give it
    // headroom over the default 15s so a cold JIT compile never times out.
    timeout: 30_000,
    headers: { 'Content-Type': 'application/json' },
});

const BASE = '/portfolio-drift-auditor';

const toError = (error: unknown, fallback: string): Error => {
    if (axios.isAxiosError(error)) {
        // Backend envelope (app/core/exceptions.py): { status, message, data }.
        const body = error.response?.data as
            | { detail?: unknown; message?: unknown; data?: unknown }
            | undefined;
        const fieldErrors = Array.isArray(body?.data) ? body?.data : body?.detail;
        if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
            const first = fieldErrors[0] as { msg?: string };
            if (first?.msg) return new Error(first.msg.replace(/^Value error, /, ''));
        }
        if (typeof body?.detail === 'string') return new Error(body.detail);
        if (typeof body?.message === 'string' && error.response?.status !== 500) {
            return new Error(body.message);
        }
        return new Error(fallback);
    }
    return error instanceof Error ? error : new Error(fallback);
};

export const useDriftAudit = () =>
    useMutation<DriftAuditResponse, Error, DriftAuditRequest>({
        mutationFn: async (payload) => {
            try {
                const { data } = await publicApiClient.post<DriftAuditResponse>(
                    `${BASE}/audit`,
                    payload,
                );
                return data;
            } catch (error) {
                throw toError(error, 'Could not run the drift audit. Please try again.');
            }
        },
    });

export const fetchDriftCoverage = async (tickers: string[]): Promise<CoverageResponse> => {
    const { data } = await publicApiClient.post<CoverageResponse>(`${BASE}/coverage`, {
        tickers,
    });
    return data;
};

// Probes how much overlapping price history the local store holds for the
// current tickers, so the form can offer only the lookback windows we can serve.
export const useDriftCoverage = (tickers: string[]) =>
    useQuery<CoverageResponse>({
        queryKey: ['drift-coverage', [...tickers].sort().join(',')],
        queryFn: () => fetchDriftCoverage(tickers),
        enabled: tickers.length > 0,
        staleTime: 5 * 60_000,
        retry: false,
    });
